import { ActionIcon, Anchor, Group, Text } from '@mantine/core';
import { IconBellOff, IconCheck, IconX } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGlobalSettings } from '@/hooks';
import { Links, PARISH_DETAILS_TAB_NAME, ROUTE_ALIASES } from '@/routing/routesAliases';
import { NOTIFICATION_STATUS, NOTIFICATION_TYPE, type NotificationView } from '@/types';
import dayjs from '@/utils/dayjs';
import styles from './NotificationItem.module.sass';

type NotificationItemProps = {
  notification: NotificationView;
  onApprove: ({ notificationId }: { notificationId: string }) => void;
  onReject: ({ notificationId }: { notificationId: string }) => void;
  onDismiss: ({ notificationId }: { notificationId: string }) => void;
  isApproveLoading: boolean;
  isRejectLoading: boolean;
  isDismissLoading: boolean;
};

const formatNotificationDate = ({ createdDate }: { createdDate: string }) => {
  return dayjs(createdDate).format('MMM D, h:mm A');
};

export const NotificationItem = ({
  notification,
  onApprove,
  onReject,
  onDismiss,
  isApproveLoading,
  isRejectLoading,
  isDismissLoading,
}: NotificationItemProps) => {
  const { t } = useTranslation('global', { keyPrefix: 'dashboardNotifications' });
  const { currentChurch } = useGlobalSettings();
  const memberName = notification.member?.name ?? '';
  const groupName = notification.group?.name ?? '';
  const canResolveJoinRequest =
    notification.type === NOTIFICATION_TYPE.JOIN_REQUEST && notification.status === NOTIFICATION_STATUS.PENDING;
  const canDismiss = !notification.read;
  const facebookReconnectPath = currentChurch?.churchId
    ? Links.parishInfo.getDetailsRoute(currentChurch.churchId, PARISH_DETAILS_TAB_NAME.INFO)
    : ROUTE_ALIASES.PARISH_INFO_ROOT;

  const messageContent = (() => {
    if (notification.type === NOTIFICATION_TYPE.TEXT_CREDITS_LOW) {
      return (
        <Text component="p" className={styles.message}>
          <Trans
            t={t}
            i18nKey="textCreditsLowMessage"
            components={{
              anchor: <Anchor component={Link} to={ROUTE_ALIASES.MESSAGES_TEXT_STATUS} fw={700} underline="always" />,
            }}
          />
        </Text>
      );
    }

    if (notification.type === NOTIFICATION_TYPE.FACEBOOK_DISCONNECTED) {
      return (
        <Text component="p" className={styles.message}>
          <Trans
            t={t}
            i18nKey="facebookDisconnectedMessage"
            components={{
              anchor: <Anchor component={Link} to={facebookReconnectPath} fw={700} underline="always" />,
            }}
          />
        </Text>
      );
    }

    if (notification.type === NOTIFICATION_TYPE.JOIN_REQUEST) {
      return (
        <Text component="p" className={styles.message}>
          <Text span fw={700}>
            {memberName}
          </Text>{' '}
          wants to join{' '}
          <Text span fw={700}>
            {groupName || 'this group'}
          </Text>
        </Text>
      );
    }

    if (notification.type === NOTIFICATION_TYPE.MEMBER_JOINED_GROUP) {
      return (
        <Text component="p" className={styles.message}>
          <Text span fw={700}>
            {memberName}
          </Text>{' '}
          joined group{' '}
          <Text span fw={700}>
            {groupName || 'a group'}
          </Text>
        </Text>
      );
    }

    if (notification.type === NOTIFICATION_TYPE.NEW_MEMBER) {
      return (
        <Text component="p" className={styles.message}>
          <Text span fw={700}>
            {memberName}
          </Text>{' '}
          favorited your church
        </Text>
      );
    }

    return (
      <Text component="p" className={styles.message}>
        You have a new notification
      </Text>
    );
  })();

  const actionButtons = (() => {
    if (!canResolveJoinRequest && !canDismiss) {
      return null;
    }

    return (
      <Group gap="xs" className={styles.actions}>
        {canResolveJoinRequest ? (
          <ActionIcon
            size="sm"
            variant="transparent"
            color="base"
            onClick={() => onReject({ notificationId: notification.id })}
            loading={isRejectLoading}
            title="Reject join request"
            aria-label="Reject join request"
          >
            <IconX size={16} />
          </ActionIcon>
        ) : null}
        {canResolveJoinRequest ? (
          <ActionIcon
            size="sm"
            variant="transparent"
            onClick={() => onApprove({ notificationId: notification.id })}
            loading={isApproveLoading}
            title="Approve join request"
            aria-label="Approve join request"
          >
            <IconCheck size={16} />
          </ActionIcon>
        ) : null}
        {canDismiss ? (
          <ActionIcon
            size="sm"
            variant="transparent"
            color="base"
            onClick={() => onDismiss({ notificationId: notification.id })}
            loading={isDismissLoading}
            title="Dismiss notification"
            aria-label="Dismiss notification"
          >
            <IconBellOff size={16} />
          </ActionIcon>
        ) : null}
      </Group>
    );
  })();

  return (
    <div className={styles.row}>
      <div className={styles.body}>
        {messageContent}
        <div className={styles.footer}>
          <Text component="p" className={styles.date} size="sm">
            {formatNotificationDate({ createdDate: notification.createdDate })}
          </Text>
          {actionButtons}
        </div>
      </div>
    </div>
  );
};
